import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Users, TrendingUp, DollarSign, AlertTriangle, FileText, Search, BarChart3, PieChart, ArrowLeftRight, ClipboardEdit } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'
import { PieChart as RechartsPieChart, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Pie } from 'recharts'

const AdminDashboard = () => {
  const { admin } = useAdminAuth()
  const [dashboardStats, setDashboardStats] = useState({
    totalCustomers: 0,
    activeLoans: 0,
    totalLoanAmount: 0,
    monthlyApplications: 0,
    financialStatements: 0
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [financialStatements, setFinancialStatements] = useState([])
  const [loading, setLoading] = useState(true)

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const [statsResponse, activityResponse, statementsResponse] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getActiveLoans(),
        apiService.getFinancialStatements()
      ])

      if (statsResponse.success) {
        setDashboardStats(statsResponse.data)
      } else {
        console.error('Failed to fetch dashboard stats:', statsResponse.message)
      }

      if (activityResponse.success) {
        // Transform loan data into activity data
        const transformedActivity = activityResponse.data.slice(0, 5).map(loan => ({
          id: loan.application_id,
          customer_name: loan.client_name,
          action: `Applied for ${loan.loan_product_name || 'loan'} - ${formatCurrency(loan.loan_amount)}`,
          timestamp: loan.application_date,
          status: loan.status === 'approved' ? 'completed' : 
                  loan.status === 'active' || loan.status === 'disbursed' ? 'completed' : 'pending'
        }))
        setRecentActivity(transformedActivity)
      } else {
        console.error('Failed to fetch active loans:', activityResponse.message)
      }

      // Fetch financial statements for mini analytics
      if (statementsResponse.success || statementsResponse.status === 'SUCCESS') {
        const rawStatements = statementsResponse.data || []
        setFinancialStatements(rawStatements.slice(0, 10)) // Limit for dashboard
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    // Convert to millions/thousands for better display
    if (amount >= 1000000) {
      return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }).format(amount / 1000000) + 'M'
    } else if (amount >= 1000) {
      return new Intl.NumberFormat('en-UG', {
        style: 'currency',
        currency: 'UGX',
        minimumFractionDigits: 0
      }).format(amount / 1000) + 'K'
    }
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const getStatementTypeData = () => {
    const typeCount = financialStatements.reduce((acc, statement) => {
      const type = statement.statementType || statement.statement_type
      if (type === 'profit_loss') {
        acc['Profit & Loss'] = (acc['Profit & Loss'] || 0) + 1
      } else if (type === 'balance_sheet') {
        acc['Balance Sheet'] = (acc['Balance Sheet'] || 0) + 1
      } else {
        acc['Other'] = (acc['Other'] || 0) + 1
      }
      return acc
    }, {})

    return Object.entries(typeCount).map(([name, value]) => ({ name, value }))
  }

  const getMonthlyData = () => {
    const monthlyCount = {}
    const last6Months = []
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      last6Months.push(monthKey)
      monthlyCount[monthKey] = 0
    }

    // Count statements by month
    financialStatements.forEach(statement => {
      const date = new Date(statement.createdAt || statement.created_at)
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      if (monthlyCount.hasOwnProperty(monthKey)) {
        monthlyCount[monthKey]++
      }
    })

    return last6Months.map(month => ({
      month,
      statements: monthlyCount[month] || 0
    }))
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 mt-2">
                Welcome back, {admin?.username} | MG: {admin?.mg_code}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/admin/settlements"
                className="btn btn-secondary flex items-center"
              >
                <ArrowLeftRight className="h-5 w-5 mr-2" />
                MG Katale Settlements
              </Link>
              <Link
                to="/admin/customers"
                className="btn btn-primary"
              >
                <Search className="h-5 w-5 mr-2" />
                Search Customers
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalCustomers}</p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <TrendingUp className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Loans</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.activeLoans}</p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-warning-100">
                <DollarSign className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">Total Loan Amount</p>
                <p className="text-xl font-bold text-gray-900 break-words">
                  {formatCurrency(dashboardStats.totalLoanAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-info-100">
                <TrendingUp className="h-6 w-6 text-info-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Monthly Applications</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.monthlyApplications}</p>
              </div>
            </div>
          </div>

          <div className="card card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Financial Statements</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.financialStatements}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/admin/financial-statements"
              className="card card-body hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Financial Statements</h3>
                  <p className="text-sm text-gray-600">View and analyze customer statements</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/product-profiles"
              className="card card-body hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <BarChart3 className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Product Profiles</h3>
                  <p className="text-sm text-gray-600">Profile members by products they posted</p>
                </div>
              </div>
            </Link>
            <Link
              to="/admin/customers"
              className="card card-body hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Customer Management</h3>
                  <p className="text-sm text-gray-600">Search and manage customers</p>
                </div>
              </div>
            </Link>

            <div className="card card-body">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-100">
                  <BarChart3 className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Reports & Analytics</h3>
                  <p className="text-sm text-gray-600">Generate financial reports</p>
                </div>
              </div>
            </div>

            <Link
              to="/admin/financial-data-entry"
              className="card card-body hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-amber-100">
                  <ClipboardEdit className="h-6 w-6 text-amber-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Financial Data Entry</h3>
                  <p className="text-sm text-gray-600">Input financial data on behalf of members</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Mini Analytics */}
        {financialStatements.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Statements by Type */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Statements by Type</h3>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsPieChart>
                    <Pie
                      data={getStatementTypeData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getStatementTypeData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Monthly Trends */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Monthly Statement Trends</h3>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={getMonthlyData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="statements" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
              </div>
              <div className="card-body">
                {recentActivity.length > 0 ? (
                  <div className="space-y-4">
                    {recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                        <div className={`p-2 rounded-full ${
                          activity.status === 'completed' ? 'bg-success-100' :
                          activity.status === 'alert' ? 'bg-danger-100' : 'bg-warning-100'
                        }`}>
                          {activity.status === 'completed' ? (
                            <TrendingUp className={`h-5 w-5 text-success-600`} />
                          ) : activity.status === 'alert' ? (
                            <AlertTriangle className="h-5 w-5 text-danger-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-warning-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{activity.customer_name}</p>
                          <p className="text-sm text-gray-600">{activity.action}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(activity.timestamp).toLocaleString()}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${
                            activity.status === 'completed' 
                              ? 'bg-success-100 text-success-800' 
                              : activity.status === 'alert'
                              ? 'bg-danger-100 text-danger-800'
                              : 'bg-warning-100 text-warning-800'
                          }`}>
                            {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              </div>
              <div className="card-body space-y-4">
                <Link
                  to="/admin/customers"
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <Search className="h-8 w-8 text-primary-600" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-900">Search Customers</h4>
                      <p className="text-sm text-gray-600">Find and manage customers</p>
                    </div>
                  </div>
                </Link>

                <Link
                  to="/admin/financial-statements"
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <FileText className="h-8 w-8 text-success-600" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-900">Financial Statements</h4>
                      <p className="text-sm text-gray-600">Review submitted statements</p>
                      {dashboardStats.financialStatements > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800 mt-1">
                          {dashboardStats.financialStatements} available
                        </span>
                      )}
                    </div>
                  </div>
                </Link>

                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-gray-400" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-500">Loan Analytics</h4>
                      <p className="text-sm text-gray-400">Coming soon</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center">
                    <FileText className="h-8 w-8 text-gray-400" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-500">Generate Reports</h4>
                      <p className="text-sm text-gray-400">Coming soon</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center">
                    <AlertTriangle className="h-8 w-8 text-gray-400" />
                    <div className="ml-4">
                      <h4 className="font-medium text-gray-500">Risk Management</h4>
                      <p className="text-sm text-gray-400">Coming soon</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="mt-8 bg-primary-50 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">MG Performance Summary</h3>
              <p className="text-gray-600 mb-4">
                Your microfinance group is performing well with {dashboardStats.totalCustomers} active customers 
                and {dashboardStats.activeLoans} active loans totaling {formatCurrency(dashboardStats.totalLoanAmount)}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary-600">
                    {dashboardStats.totalCustomers > 0 ? Math.round((dashboardStats.activeLoans / dashboardStats.totalCustomers) * 100) : 0}%
                  </p>
                  <p className="text-sm text-gray-600">Loan Utilization</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-success-600">{dashboardStats.monthlyApplications}</p>
                  <p className="text-sm text-gray-600">This Month's Applications</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-warning-600">{dashboardStats.financialStatements}</p>
                  <p className="text-sm text-gray-600">Financial Statements</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
